import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import {
  HeicConversionError,
  MAX_HEIC_BATCH_FILES,
  boundedHeicOutput,
  convertHeic,
  downloadBlob,
  formatSize,
  outputFilename,
  uniqueOutputFilename,
  validateHeicFile,
  type HeicErrors,
  type HeicOutputFormat,
  heicOutputConfig,
  heicAnalyticsDimensions,
} from '../model/heicProcessing'
import { LocalizedProcessingBadge, useT } from '@/shared/i18n'
import { ToolHero } from '@/shared/ui/ToolHero'

type HeicStatus = 'pending' | 'converting' | 'done' | 'error'

interface HeicCopy {
  eyebrow: string
  title: string
  description: string
  dropTitle: string
  dropDescription: string
  chooseFiles: string
  qualityLabel?: string
  multiFrameWarning: string
  privacyNote: string
  qualityHowTo: string
  outputLabel: string
  outputHelp: string
  viewerLink: string
  errorsText: HeicErrors
  faq: Array<{ question: string; answer: string }>
}

interface HeicEntry {
  id: string
  file: File
  originalSize: number
  convertedBlob: Blob | null
  convertedUrl: string | null
  convertedSize: number | null
  status: HeicStatus
  error?: string
  errorKind?: 'validation' | 'conversion'
  warning?: string
}

interface HeicConverterPageProps {
  copy: HeicCopy
  initialOutput?: HeicOutputFormat
  convert?: typeof convertHeic
}

let nextId = 0

function uid(): string {
  return `heic-${++nextId}-${Date.now()}`
}

function revokeEntry(entry: HeicEntry): void {
  if (entry.convertedUrl) {
    URL.revokeObjectURL(entry.convertedUrl)
  }
}

function resetEntryForOutput(entry: HeicEntry): HeicEntry {
  if (entry.status === 'error' && entry.errorKind === 'validation') return entry
  revokeEntry(entry)
  return {
    ...entry,
    convertedBlob: null,
    convertedUrl: null,
    convertedSize: null,
    status: 'pending',
    error: undefined,
    errorKind: undefined,
    warning: undefined,
  }
}

export function HeicConverterPage({
  copy,
  initialOutput = 'jpg',
  convert = convertHeic,
}: HeicConverterPageProps) {
  const t = useT()
  const [outputFormat, setOutputFormat] = useState<HeicOutputFormat>(
    boundedHeicOutput(initialOutput),
  )
  const config = heicOutputConfig(outputFormat)
  const [files, setFiles] = useState<HeicEntry[]>([])
  const [quality, setQuality] = useState(config.defaultQuality ?? 85)
  const [isConverting, setIsConverting] = useState(false)
  const [dropActive, setDropActive] = useState(false)
  const [batchError, setBatchError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const previousQualityRef = useRef(quality)
  const filesRef = useRef<HeicEntry[]>([])
  const conversionRef = useRef<AbortController | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    filesRef.current = files
  }, [files])

  useEffect(() => {
    if (!config.showQuality || previousQualityRef.current === quality) return
    previousQualityRef.current = quality

    setFiles((prev) =>
      prev.map((entry) => {
        if (entry.status === 'pending') return entry
        return resetEntryForOutput(entry)
      }),
    )
  }, [config.showQuality, quality])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      conversionRef.current?.abort()
      filesRef.current.forEach(revokeEntry)
    }
  }, [])

  const resetConvertedResults = useCallback(() => {
    setFiles((prev) =>
      prev.map(resetEntryForOutput),
    )
  }, [])

  const handleOutputChange = useCallback((format: HeicOutputFormat) => {
    if (isConverting || format === outputFormat) return
    setOutputFormat(format)
    setQuality(heicOutputConfig(format).defaultQuality ?? 85)
    resetConvertedResults()
  }, [isConverting, outputFormat, resetConvertedResults])

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      if (isConverting) return

      const availableSlots = Math.max(0, MAX_HEIC_BATCH_FILES - filesRef.current.length)
      const acceptedIncoming = Array.from(incoming).slice(0, availableSlots)
      if (acceptedIncoming.length < incoming.length) {
        toolError(config.utilityKey, 'too_many_files', { output_format: outputFormat })
        setBatchError(
          copy.errorsText.tooMany ?? `Maximum ${MAX_HEIC_BATCH_FILES} files.`,
        )
      } else {
        setBatchError(null)
      }
      const entries = acceptedIncoming.map<HeicEntry>((file) => {
        const validationError = validateHeicFile(file, copy.errorsText)
        if (validationError) {
          toolError(config.utilityKey, validationError.code, { output_format: outputFormat })
          return {
            id: uid(),
            file,
            originalSize: file.size,
            convertedBlob: null,
            convertedUrl: null,
            convertedSize: null,
            status: 'error',
            error: validationError.message,
            errorKind: 'validation',
          }
        }

        return {
          id: uid(),
          file,
          originalSize: file.size,
          convertedBlob: null,
          convertedUrl: null,
          convertedSize: null,
          status: 'pending',
        }
      })

      setFiles((prev) => [...prev, ...entries])
    },
    [config.utilityKey, copy.errorsText, isConverting, outputFormat],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        addFiles(event.target.files)
        event.target.value = ''
      }
    },
    [addFiles],
  )

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(true)
  }, [])

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(false)
  }, [])

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setDropActive(false)

      if (isConverting) return

      if (event.dataTransfer.files.length > 0) {
        addFiles(event.dataTransfer.files)
      }
    },
    [addFiles, isConverting],
  )

  const removeFile = useCallback((id: string) => {
    if (isConverting) return

    setFiles((prev) => {
      const entry = prev.find((file) => file.id === id)
      if (entry) {
        revokeEntry(entry)
      }
      return prev.filter((file) => file.id !== id)
    })
  }, [isConverting])

  const clearAll = useCallback(() => {
    if (isConverting) return

    files.forEach(revokeEntry)
    setFiles([])
    setBatchError(null)
    nextId = 0
  }, [files, isConverting])

  const handleConvert = useCallback(async () => {
    const controller = new AbortController()
    conversionRef.current?.abort()
    conversionRef.current = controller
    const analytics = heicAnalyticsDimensions(outputFormat)
    toolInvoke(config.utilityKey, analytics)
    setIsConverting(true)

    const startTime = performance.now()
    const pending = files.filter((file) => file.status === 'pending')
    const updated = [...files]
    let runSuccessCount = 0

    for (const entry of pending) {
      const index = updated.findIndex((file) => file.id === entry.id)
      if (index === -1) continue

      updated[index] = { ...entry, status: 'converting', error: undefined }
      setFiles([...updated])

      try {
        const { blob, frames } = await convert(
          entry.file,
          config,
          quality,
          copy.errorsText,
          controller.signal,
        )
        if (controller.signal.aborted || !mountedRef.current) return
        const convertedUrl = URL.createObjectURL(blob)
        updated[index] = {
          ...entry,
          convertedBlob: blob,
          convertedUrl,
          convertedSize: blob.size,
          status: 'done',
          warning: frames > 1 ? copy.multiFrameWarning : undefined,
        }
        runSuccessCount += 1
        setFiles([...updated])
      } catch (error) {
        const code =
          error instanceof HeicConversionError ? error.code : 'conversion_failed'
        if (code === 'cancelled' || controller.signal.aborted) {
          return
        }
        toolError(config.utilityKey, code, analytics)
        updated[index] = {
          ...entry,
          status: 'error',
          error: error instanceof Error ? error.message : copy.errorsText.generic,
          errorKind: 'conversion',
        }
        setFiles([...updated])
      }
    }

    const durationMs = Math.round(performance.now() - startTime)
    if (runSuccessCount > 0) {
      toolSuccess(config.utilityKey, durationMs, analytics)
    } else {
      toolError(config.utilityKey, 'all_failed', analytics)
    }

    if (mountedRef.current && conversionRef.current === controller) {
      conversionRef.current = null
      setIsConverting(false)
    }
  }, [config, convert, copy.errorsText, copy.multiFrameWarning, files, outputFormat, quality])

  const handleDownloadOne = useCallback((entry: HeicEntry) => {
    if (!entry.convertedBlob) return
    downloadBlob(
      entry.convertedBlob,
      outputFilename(entry.file.name, config.outputExtension),
    )
  }, [config.outputExtension])

  const handleDownloadAll = useCallback(async () => {
    const done = files.filter((file) => file.status === 'done' && file.convertedBlob)
    if (done.length === 0) return

    const { default: JSZip } = await import('jszip')
    const zip = new JSZip()
    const usedNames = new Set<string>()

    for (const entry of done) {
      zip.file(
        uniqueOutputFilename(entry.file.name, config.outputExtension, usedNames),
        entry.convertedBlob!,
      )
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' })
    downloadBlob(zipBlob, config.zipFilename)
  }, [config.outputExtension, config.zipFilename, files])

  const stats = useMemo(() => {
    const pending = files.filter((file) => file.status === 'pending').length
    const done = files.filter((file) => file.status === 'done').length
    const errors = files.filter((file) => file.status === 'error').length
    const totalOriginal = files.reduce((sum, file) => sum + file.originalSize, 0)
    const totalConverted = files
      .filter((file) => file.convertedSize !== null)
      .reduce((sum, file) => sum + (file.convertedSize ?? 0), 0)
    return { pending, done, errors, totalOriginal, totalConverted }
  }, [files])

  const canConvert = files.length > 0 && !isConverting && stats.pending > 0

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero
        eyebrow={copy.eyebrow}
        title={copy.title}
        description={copy.description}
        chips={[`HEIC, HEIF -> ${config.outputLabel}`, t.common.maxFileSizePerFile]}
        collapsed={files.length > 0}
      >
        <a
          href="/otkryt-heic"
          className="mt-2 inline-flex text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          {copy.viewerLink}
        </a>
      </ToolHero>

      <div
        className={cn(
          'grid grid-cols-1 gap-6',
          files.length > 0 && 'lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start',
        )}
      >
        <div className="min-w-0 space-y-6">
          <Card
            className={cn(
              'mx-auto max-w-3xl transition-colors',
              files.length > 0 && 'max-w-none',
              dropActive && 'border-primary bg-primary/5',
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <CardContent className="p-4 text-center sm:p-8">
              <div className="mx-auto mb-5 hidden h-16 w-16 place-items-center sm:grid rounded-md border bg-background text-xs font-semibold text-muted-foreground">
                HEIC
              </div>
              <p className="mb-2 hidden text-lg font-medium [@media(pointer:fine)]:block">{copy.dropTitle}</p>
              <p
                className={cn(
                  'mx-auto mb-5 max-w-md text-sm leading-6 text-muted-foreground',
                  files.length > 0 && 'hidden sm:block',
                )}
              >
                {copy.dropDescription}
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".heic,.heif,image/heic,image/heif,image/heic-sequence,image/heif-sequence"
                multiple
                disabled={isConverting}
                className="hidden"
                onChange={handleInputChange}
              />
              <div className="flex flex-wrap justify-center gap-3">
                <Button
                  disabled={isConverting}
                  onClick={() => inputRef.current?.click()}
                >
                  {copy.chooseFiles}
                </Button>
                {files.length > 0 && (
                  <Button
                    variant="outline"
                    disabled={isConverting}
                    onClick={clearAll}
                  >
                    {t.converter.clearAll}
                  </Button>
                )}
              </div>
              {batchError && (
                <p role="alert" className="mt-4 text-sm text-destructive">
                  {batchError}
                </p>
              )}
            </CardContent>
          </Card>

          {files.length > 0 && (
            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold tracking-normal">
                  {t.converter.filesTitle} ({files.length})
                </h2>
                <div className="flex gap-2">
                  {stats.done > 0 && (
                    <Button variant="outline" size="sm" onClick={handleDownloadAll}>
                      {t.converter.downloadAll} ({stats.done})
                    </Button>
                  )}
                  {canConvert && (
                    <Button size="sm" onClick={handleConvert}>
                      {t.common.convert} ({stats.pending})
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {files.map((entry) => {
                  const convertedDelta =
                    entry.originalSize > 0 && entry.convertedSize !== null
                      ? Math.round(
                          ((entry.convertedSize - entry.originalSize) / entry.originalSize) *
                            100,
                        )
                      : null

                  return (
                    <Card key={entry.id} className="overflow-hidden shadow-sm">
                      <CardContent className="p-0">
                        <div className="grid aspect-video place-items-center bg-muted">
                          {entry.convertedUrl ? (
                            <img
                              src={entry.convertedUrl}
                              alt={entry.file.name}
                              className="h-full w-full object-contain"
                            />
                          ) : (
                            <div className="rounded-md border bg-background px-3 py-2 text-xs font-semibold text-muted-foreground">
                              HEIC
                            </div>
                          )}
                        </div>

                        <div className="space-y-4 p-4">
                          <div className="min-w-0">
                            <div
                              className="truncate text-sm font-medium"
                              title={entry.file.name}
                            >
                              {entry.file.name}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span className="rounded-md border bg-background px-2 py-1 text-xs font-medium text-muted-foreground">
                                HEIC/HEIF
                              </span>
                              <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                                {formatSize(entry.originalSize)}
                              </span>
                              {entry.convertedSize !== null && (
                                <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                                  {formatSize(entry.convertedSize)}
                                  {convertedDelta !== null && (
                                    <span
                                      className={cn(
                                        'ml-1 font-medium',
                                        convertedDelta <= 0
                                          ? 'text-primary'
                                          : 'text-muted-foreground',
                                      )}
                                    >
                                      {convertedDelta > 0 ? '+' : ''}
                                      {convertedDelta}%
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>

                          {entry.status === 'converting' && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                              {t.common.converting}
                            </div>
                          )}

                          {entry.status === 'done' && entry.convertedBlob && (
                            <div className="space-y-3">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <span className="text-sm font-medium text-primary">
                                  {t.common.done}
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDownloadOne(entry)}
                                >
                                  {t.common.download}
                                </Button>
                              </div>
                              {entry.warning && (
                                <p className="text-xs leading-5 text-muted-foreground">
                                  {entry.warning}
                                </p>
                              )}
                            </div>
                          )}

                          {entry.status === 'error' && (
                            <div className="text-sm text-destructive">
                              {entry.error ?? t.common.errorConverting}
                            </div>
                          )}

                          {entry.status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={isConverting}
                              className="text-destructive hover:text-destructive"
                              onClick={() => removeFile(entry.id)}
                            >
                              {t.common.remove}
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </section>
          )}
        </div>

        {files.length > 0 && (
          <aside className="min-w-0 space-y-4 lg:sticky lg:top-24">
            <Card>
              <CardContent className="space-y-5 p-5">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold tracking-normal">
                    {t.converter.settings}
                  </h2>
                  <LocalizedProcessingBadge variant="local" />
                </div>

                <fieldset className="space-y-2">
                  <legend className="text-sm font-medium">{copy.outputLabel}</legend>
                  <div className="grid grid-cols-3 gap-2">
                    {(['jpg', 'png', 'webp'] as const).map((format) => (
                      <Button
                        key={format}
                        type="button"
                        variant={outputFormat === format ? 'default' : 'outline'}
                        disabled={isConverting}
                        aria-pressed={outputFormat === format}
                        onClick={() => handleOutputChange(format)}
                      >
                        {format.toUpperCase()}
                      </Button>
                    ))}
                  </div>
                  <p className="text-xs leading-5 text-muted-foreground">
                    {copy.outputHelp}
                  </p>
                </fieldset>

                {config.showQuality && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium">
                        {copy.qualityLabel ?? t.converter.jpegQuality}
                      </span>
                      <span className="text-sm tabular-nums text-muted-foreground">
                        {quality}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min={40}
                      max={100}
                      step={1}
                      value={quality}
                      disabled={isConverting}
                      onChange={(event) => setQuality(Number(event.target.value))}
                      className="w-full accent-primary"
                    />
                  </div>
                )}

                <Button
                  className="w-full"
                  size="lg"
                  onClick={handleConvert}
                  disabled={!canConvert}
                >
                  {isConverting
                    ? t.common.converting
                    : stats.pending > 0
                      ? `${t.common.convert} (${stats.pending})`
                      : t.converter.noFilesQueued}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 p-5">
                <h2 className="text-lg font-semibold tracking-normal">{t.converter.summary}</h2>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-md bg-muted p-3">
                    <div className="font-semibold tabular-nums">{stats.pending}</div>
                    <div className="text-xs text-muted-foreground">{t.converter.pending}</div>
                  </div>
                  <div className="rounded-md bg-muted p-3">
                    <div className="font-semibold tabular-nums">{stats.done}</div>
                    <div className="text-xs text-muted-foreground">{t.converter.done}</div>
                  </div>
                  <div className="rounded-md bg-muted p-3">
                    <div className="font-semibold tabular-nums">{stats.errors}</div>
                    <div className="text-xs text-muted-foreground">{t.converter.errors}</div>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex justify-between gap-3">
                    <span>{t.converter.originalSize}</span>
                    <strong className="font-medium text-foreground">
                      {formatSize(stats.totalOriginal)}
                    </strong>
                  </div>
                  {stats.done > 0 && (
                    <div className="flex justify-between gap-3">
                      <span>{t.converter.convertedSize}</span>
                      <strong className="font-medium text-foreground">
                        {formatSize(stats.totalConverted)}
                      </strong>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <p className="rounded-md border bg-card px-3 py-2 text-xs leading-5 text-muted-foreground">
              {copy.privacyNote}
            </p>
          </aside>
        )}
      </div>

      <section className="mt-12 grid gap-4 border-t pt-8 lg:grid-cols-3">
        {copy.faq.map((item) => (
          <div key={item.question}>
            <h2 className="text-base font-semibold tracking-normal">{item.question}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {item.answer}
            </p>
          </div>
        ))}
      </section>
    </div>
  )
}
